import {Actions, Separator, type Action} from 'platform/components';
import {Box, HStack, Icon, Show, Text} from 'platform/foundation';

import type {ReactNode} from 'react';

import {isNotNilOrEmpty} from 'ramda-adjunct';

import type {TestIdProps} from 'shared/web';

export type ConfiguratorActionBarProps = TestIdProps & {
  leading: ReactNode;
  hint?: string;
  hintTone?: 'neutral' | 'warning';
  leadingActions?: Action[];
  trailingActions?: Action[];
};

export function ConfiguratorActionBar(props: ConfiguratorActionBarProps) {
  const isWarning = props.hintTone === 'warning';

  return (
    <Box position="sticky" bottom={0} zIndex="CONTENT_LOADER" paddingTop={3} paddingBottom={3}>
      <Box
        backgroundColor="general.white"
        border="1px solid"
        borderColor="general.separator"
        borderRadius="large"
        boxShadow="elevation_3"
        paddingVertical={3}
        paddingLeft={4}
        paddingRight={4}
        data-testid={props['data-testid']}
      >
        <HStack align="center" justify="space-between" spacing={6}>
          {/* The status block keeps its width; a long hint wraps instead of squeezing it. */}
          <HStack align="center" spacing={5} shrink={0}>
            <Actions actions={props.leadingActions} size="default" />
            <Show when={isNotNilOrEmpty(props.leadingActions)}>
              <Box height={8}>
                <Separator orientation="vertical" spacing={0} />
              </Box>
            </Show>
            {props.leading}
          </HStack>
          <HStack align="center" spacing={4}>
            <Show when={isNotNilOrEmpty(props.hint)}>
              <HStack align="center" spacing={1}>
                <Icon
                  value={isWarning ? 'alert/warning' : 'action/info_outline'}
                  size={4}
                  color={isWarning ? 'severity.warning' : 'text.secondary'}
                />
                <Text size="small" color={isWarning ? 'warning' : 'secondary'}>
                  {props.hint}
                </Text>
              </HStack>
            </Show>
            <Actions actions={props.trailingActions} size="default" />
          </HStack>
        </HStack>
      </Box>
    </Box>
  );
}
